import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  createAvatar,
  updateAvatar,
  deleteAvatar,
  reorderAvatars,
  fetchComposerSnapshot,
  saveVoiceProfile,
  type AvatarUpdateInput,
} from "@/lib/composerCore";
import { useVoiceProfileDraft, useAvatarDraft } from "./composerProfileDrafts";
import { emptySnapshot, splitLines, type Snapshot } from "./composerPageHelpers";

export function useComposerSnapshot(workspaceId: string | null) {
  const [snapshot, setSnapshot] = useState<Snapshot>(emptySnapshot);
  const [loading, setLoading] = useState(true);
  const resolvedWorkspaceRef = useRef<string | null>(null);
  const [savingVoice, setSavingVoice] = useState(false);
  const [creatingAvatar, setCreatingAvatar] = useState(false);

  const [voiceDraft, setVoiceDraft] = useVoiceProfileDraft(snapshot.voiceProfile);
  const [avatarDraft, setAvatarDraft, resetAvatarDraft] = useAvatarDraft();

  // Internal: re-pull the snapshot. When `showLoading` is false, we skip
  // the `loading=true` flash so background refreshes (e.g. after attaching
  // a source to a footnote) don't cover the canvas with the skeleton.
  const runRefresh = async (showLoading: boolean) => {
    if (!workspaceId) {
      setSnapshot(emptySnapshot);
      resolvedWorkspaceRef.current = null;
      setLoading(false);
      return;
    }
    const initialLoad = resolvedWorkspaceRef.current !== workspaceId;
    if (showLoading && initialLoad) setLoading(true);
    try {
      const next = await fetchComposerSnapshot(workspaceId);
      setSnapshot(next);
      resolvedWorkspaceRef.current = workspaceId;
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      if (showLoading && initialLoad) setLoading(false);
    }
  };

  const refresh = () => runRefresh(true);
  /** Background refresh — no loading skeleton. */
  const refreshQuiet = () => runRefresh(false);

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const handleSaveVoice = async () => {
    if (!workspaceId) return;
    setSavingVoice(true);
    try {
      await saveVoiceProfile(workspaceId, voiceDraft, snapshot.voiceProfile?.id);
      toast.success("Voice profile saved");
      await refresh();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setSavingVoice(false);
    }
  };

  const handleCreateAvatar = async () => {
    if (!workspaceId || !avatarDraft.name.trim()) return;
    setCreatingAvatar(true);
    try {
      await createAvatar(workspaceId, {
        name: avatarDraft.name,
        experienceLevel: avatarDraft.experienceLevel,
        summary: avatarDraft.summary,
        painPoints: splitLines(avatarDraft.painPoints),
        desires: splitLines(avatarDraft.desires),
        toneGuidance: avatarDraft.toneGuidance,
        vocabularyAssumptions: avatarDraft.vocabularyAssumptions,
        messagingPosture: avatarDraft.messagingPosture,
        voiceModeAWeight: avatarDraft.voiceModeAWeight,
        toneAdjustments: avatarDraft.toneAdjustments,
        vocabularyAdjustments: avatarDraft.vocabularyAdjustments,
      });
      resetAvatarDraft();
      toast.success("Avatar added");
      await refresh();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setCreatingAvatar(false);
    }
  };

  const handleUpdateAvatar = async (avatarId: string, values: AvatarUpdateInput) => {
    try {
      await updateAvatar(avatarId, values);
      toast.success("Avatar updated");
      await refresh();
    } catch (error) {
      toast.error((error as Error).message);
      throw error;
    }
  };

  const handleDeleteAvatar = async (avatarId: string) => {
    try {
      await deleteAvatar(avatarId);
      toast.success("Avatar deleted");
      await refresh();
    } catch (error) {
      toast.error((error as Error).message);
      throw error;
    }
  };

  const handleReorderAvatars = async (orderedIds: string[]) => {
    if (!workspaceId) return;
    try {
      await reorderAvatars(workspaceId, orderedIds);
      await refreshQuiet();
    } catch (error) {
      toast.error((error as Error).message);
      await refresh();
    }
  };

  return {
    snapshot,
    setSnapshot,
    loading,
    refresh,
    refreshQuiet,
    voiceDraft,
    setVoiceDraft,
    savingVoice,
    handleSaveVoice,
    avatarDraft,
    setAvatarDraft,
    creatingAvatar,
    handleCreateAvatar,
    handleUpdateAvatar,
    handleDeleteAvatar,
    handleReorderAvatars,
  };
}
